import {
  Button,
  DialogActions,
  DialogContent,
  FormControl,
  FormHelperText,
  Grid,
  Theme,
} from "@mui/material";
import { makeStyles } from "helpers/StyleHelper";
import { useTheme } from "@mui/material/styles";
import Alert from "@mui/material/Alert";
import Field from "components/Field";
import { FORM_ERROR } from "final-form";
import useGlobalContext from "hooks/globalData";
import useTrashablePromises from "hooks/useTrashablePromises";
import { pick } from "lodash";
import React, { useMemo, useState } from "react";
import { withTypes } from "react-final-form";
import { useIntl } from "react-intl";
import {
  SaveResponse,
  SkuResource,
  SkuResourceRequest,
} from "services/KnowfullyAdminClient";
import { Severity, SnackbarMessageProps } from "types/common-types";
import { SkuFormProps } from "types/sku-related-types";
import { isEmptyOrSpaces } from "utils/formUtils";
import translateError from "utils/translateError";
import SelectResource from "components/SelectResource";
import SelectResourceType from "./SelectResourceType";

const useStyles = makeStyles((theme: Theme) => ({
  feedback: {
    width: "100%",
    margin: "20px 0 20px 0",
  },
  subtitle: {
    margin: theme.spacing(3, 0, 2),
    color: theme.palette.primary.main,
    fontWeight: "bold",
  },
  checkboxContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
}));

type SkuResourceFormProps = {
  skuResources: SkuResource[];
  onCancel?: () => void;
  setSnackbarMessage: (snackbarMessage: SnackbarMessageProps | null) => void;
  onResourceCreated: (saveResponse: SaveResponse) => void;
};

type SkuResourceFormValues = {
  skuId: string;
  resourceTypeId: string;
  resourceId: string;
  resourceName: string;
};

type ResourceWithId = {
  id?: string | null;
  value: string;
};

const { Form } = withTypes<SkuResourceFormValues>();

const SkuResourceForm = ({
  sku,
  skuResources,
  onCancel,
  setSnackbarMessage,
  onResourceCreated,
  onSkuUpdated,
  ...rest
}: SkuResourceFormProps & SkuFormProps) => {
  const { formatMessage: t } = useIntl();
  const styles = useStyles(useTheme());
  const { registerPromise } = useTrashablePromises();
  const { skuService } = useGlobalContext();
  const [resource, setResource] = useState<ResourceWithId | null>(null);

  const init = useMemo<SkuResourceFormValues>(
    () => ({
      skuId: pick(sku.id! ?? "", ["id"]),
      resourceTypeId: "",
      resourceId: "",
      resourceName: "",
    }),
    [sku]
  );

  return useMemo(() => {
    const onSubmit = async (values: SkuResourceFormValues) => {
      if (isEmptyOrSpaces(resource?.value!)) {
        setSnackbarMessage(
          new SnackbarMessageProps(
            t({ id: "skuResourceEmpty" }),
            Severity.Warning
          )
        );
        return;
      }

      if (resource?.value) {
        values.resourceId = resource?.id!;
        values.resourceName = resource?.value!;
      }

      const existingResource = skuResources.find(
        (x) =>
          (x.resource?.id! === values.resourceId! ||
            x.resource?.name === values.resourceName) &&
          x.resourceType?.id! === values.resourceTypeId!
      );

      if (existingResource !== undefined) {
        setSnackbarMessage(
          new SnackbarMessageProps(
            t({ id: "skuFacultyRepeated" }),
            Severity.Warning
          )
        );
        return;
      }

      try {
        const response = await registerPromise(
          skuService.saveSkuResource(sku.id!, SkuResourceRequest.fromJS(values))
        );

        setSnackbarMessage(
          new SnackbarMessageProps(t({ id: "skuFacultyCreated" }))
        );
        onResourceCreated(response!);
        onSkuUpdated && onSkuUpdated(sku.id!);

        return response;
      } catch (err: any) {
        console.log(err);
        return { [FORM_ERROR]: translateError(err, t) };
      }
    };

    return (
      <Form
        onSubmit={onSubmit}
        initialValues={init}
        render={({ handleSubmit, submitting, submitError }) => (
          <form onSubmit={handleSubmit}>
            <DialogContent dividers>
              <Grid container spacing={1}>
                <Field name="resourceTypeId" required>
                  {({ input, meta }) => (
                    <Grid item xs={12}>
                      <FormControl
                        fullWidth
                        error={meta.error && meta.submitFailed}
                      >
                        <SelectResourceType
                          {...input}
                          textFieldProps={{
                            error: meta.error && meta.submitFailed,
                          }}
                          required
                        />
                        {meta.error && meta.submitFailed && (
                          <FormHelperText>{meta.error}</FormHelperText>
                        )}
                      </FormControl>
                    </Grid>
                  )}
                </Field>

                <Field name="resourceId" required>
                  {({ input, meta }) => {
                    return (
                      <Grid item xs={12}>
                        <FormControl
                          fullWidth
                          error={meta.error && meta.submitFailed}
                        >
                          <SelectResource
                            {...input}
                            {...rest}
                            textFieldProps={{
                              error: meta.error && meta.submitFailed,
                            }}
                            required
                            onChange={(value) => {
                              setResource({
                                id: value?.id,
                                value: value?.name ?? "?",
                              });
                              input.onChange(value?.id);
                            }}
                          />
                          {meta.error && meta.submitFailed && (
                            <FormHelperText>{meta.error}</FormHelperText>
                          )}
                        </FormControl>
                      </Grid>
                    );
                  }}
                </Field>

                {submitError && (
                  <Alert sx={styles.feedback} severity="error">
                    {submitError}
                  </Alert>
                )}
              </Grid>
            </DialogContent>

            <DialogActions>
              <Button disabled={submitting} onClick={onCancel}>
                {t({ id: "cancelButton" })}
              </Button>

              <Button type="submit" color="primary" disabled={submitting}>
                {t({ id: "saveButton" })}
              </Button>
            </DialogActions>
          </form>
        )}
      />
    );
  }, [
    styles.feedback,
    init,
    onCancel,
    onResourceCreated,
    onSkuUpdated,
    registerPromise,
    resource?.id,
    resource?.value,
    rest,
    setSnackbarMessage,
    sku.id,
    skuResources,
    skuService,
    t,
  ]);
};

export default SkuResourceForm;
